import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import CourseDetail from "@/pages/CourseDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCourseEditor from "@/pages/AdminCourseEditor";
import AdminUserResults from "@/pages/AdminUserResults";
import AdminAssignments from "@/pages/AdminAssignments";
import Library from "@/pages/Library";
import BookReader from "@/pages/BookReader";
import FinancingPartners from "@/pages/FinancingPartners";
import PartnerDetail from "@/pages/PartnerDetail";
import AdminFinancing from "@/pages/AdminFinancing";
import ContentHub from "@/pages/ContentHub";
import ProcessCategories from "@/pages/ProcessCategories";
import ProcessCategoryDetail from "@/pages/ProcessCategoryDetail";
import AdminProcesses from "@/pages/AdminProcesses";
import CommunityRedirect from "@/pages/CommunityRedirect";
import Rewards from "@/pages/Rewards";
import AdminRewards from "@/pages/AdminRewards";
import LanguagesLanding from "@/pages/LanguagesLanding";
import LanguagesOnboarding from "@/pages/LanguagesOnboarding";
import LanguageMap from "@/pages/LanguageMap";
import LanguageLesson from "@/pages/LanguageLesson";
import LanguageMaterial from "@/pages/LanguageMaterial";
import AdminLanguages from "@/pages/AdminLanguages";
import AdminEvaluations from "@/pages/AdminEvaluations";
import UserEvaluations from "@/pages/UserEvaluations";
import ManagerTeam from "@/pages/ManagerTeam";
import AdminSMS from "@/pages/AdminSMS";
import AdminSettings from "@/pages/AdminSettings";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ScenarioPlayer from "@/pages/ScenarioPlayer";
import AdminScenarios from "@/pages/AdminScenarios";
import AdminScenarioEditor from "@/pages/AdminScenarioEditor";
import AdminScenarioAnalytics from "@/pages/AdminScenarioAnalytics";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsConditions from "@/pages/TermsConditions";
import ConversationPicker from "@/pages/ConversationPicker";
import ConversationChat from "@/pages/ConversationChat";
import AppLayout from "@/components/AppLayout";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFFFF]">
        <div className="text-[#374151]">Cargando...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Force users with must_change_password=true to change it before anything else.
  if (user.must_change_password && loc.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL synchronous check - process session_id BEFORE other routes
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsConditions />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CourseDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminDashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminSettings />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scenarios/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ScenarioPlayer />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scenarios"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminScenarios />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scenarios/:id"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminScenarioEditor />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scenarios/:id/analytics"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminScenarioAnalytics />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/courses/:id"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminCourseEditor />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminUserResults />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assignments"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminAssignments />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/biblioteca"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Library />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/biblioteca/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <BookReader />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/financiamiento"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FinancingPartners />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/financiamiento/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PartnerDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/financiamiento"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminFinancing />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seccion/:section"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ContentHub />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procesos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ProcessCategories />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procesos/:slug"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ProcessCategoryDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/procesos"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminProcesses />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/comunidad"
        element={
          <ProtectedRoute>
            <CommunityRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recompensas"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Rewards />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/recompensas"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminRewards />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/idiomas"
        element={
          <ProtectedRoute>
            <AppLayout>
              <LanguagesLanding />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/idiomas/onboarding"
        element={
          <ProtectedRoute>
            <AppLayout>
              <LanguagesOnboarding />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/idiomas/leccion/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <LanguageLesson />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/idiomas/:lang"
        element={
          <ProtectedRoute>
            <AppLayout>
              <LanguageMap />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/idiomas/conversar/:lang"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ConversationPicker />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/idiomas/conversar/:lang/sesion/:sessionId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ConversationChat />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/idiomas/material/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <LanguageMaterial />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/idiomas"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminLanguages />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/evaluaciones"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminEvaluations target="salesperson" />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eval-managers"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminEvaluations target="manager" />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evaluaciones"
        element={
          <ProtectedRoute>
            <AppLayout>
              <UserEvaluations />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mi-equipo"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ManagerTeam />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sms"
        element={
          <ProtectedRoute adminOnly>
            <AppLayout>
              <AdminSMS />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App font-body bg-[#FFFFFF] text-[#1A1A1A] min-h-screen">
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </I18nProvider>
    </div>
  );
}

export default App;
